import React, { FC, useEffect, useRef, useState } from 'react';
import styles from './index.module.scss';
import { Graph } from '@antv/g6';
import { registerG6 } from './registerG6';
import { useRequest } from 'ahooks';
import {
  apiV2PowerRoomConfigGetPrimaryWiringDiagramByRoomIdPost,
  apiV2PowerRoomConfigGetPrimaryWiringDiagramDataByRoomIdPost,
} from '@maxtropy/device-customer-apis-v2';
import { formatAllEdges, formatAllNodes, formatData, setPadding } from '../../utils';
import { Button } from '@maxtropy/components';
import { FullscreenExitOutlined, FullscreenOutlined } from '@ant-design/icons';
import _, { isNil } from 'lodash';
import { convertHighDataToGraph, convertLowDataToGraph, convertRestTreeToGraph, splitRowsByWidth } from './drawFunc';
import CircuitDataModal from '../CircuitDataModal';
import SwitchControlModal from '../SwitchControlModal';
import { SwitchStatusType, SwitchType } from '@/api/circuit';
import { Space } from 'antd';
import { useNavigate } from 'react-router-dom';

interface IWiringDiagramHighV {
  roomId?: number;
}

const WiringDiagramHighV: FC<IWiringDiagramHighV> = ({ roomId }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const graphRef = useRef<Graph>();
  const resizeObserverRef = useRef<any>();
  const navigate = useNavigate();
  const [nodesAndEdges, setNodesAndEdges] = useState<any>();
  // 全屏
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  // 数据弹窗展示
  const [showCircuitModal, setShowCircuitModal] = useState<boolean>(false);
  const [circuitInfo, setCircuitInfo] = useState<any>();
  // 开关控制
  const [controlSwitchModal, setControlSwitchModal] = useState<boolean>(false);
  // 展示形态A，B
  const [showType, setShowType] = useState<string>('A');

  const handleFullScreen = () => {
    setIsFullScreen(document.fullscreenElement !== null);
  };

  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullScreen);
    return () => document.removeEventListener('fullscreenchange', handleFullScreen);
  }, []);

  //全屏
  const fullBtn = () => {
    // if (containerRef.current) {
    //   isFullScreen ? cancelFullscreen() : goFullscreen(containerRef.current);
    // }
    setIsFullScreen(!isFullScreen);
  };

  // 接口请求(一次接线图)
  const { data } = useRequest(
    () =>
      apiV2PowerRoomConfigGetPrimaryWiringDiagramByRoomIdPost({
        id: roomId,
      }).then(res => {
        return res;
      }),
    {
      ready: !!roomId,
      refreshDeps: [roomId],
    }
  );
  // 接口请求(一次接线图数据点数据)
  const { data: dataProperty } = useRequest(
    () =>
      apiV2PowerRoomConfigGetPrimaryWiringDiagramDataByRoomIdPost({
        id: roomId,
      }).then(res => {
        return res;
      }),
    {
      ready: !!roomId,
      refreshDeps: [roomId],
    }
  );

  useEffect(() => {
    registerG6();
  }, []);

  // useEffect(() => {
  //   if (!data) return;
  //   const finalFormatData = formatData(data);
  //   const { nodes: highNodes } = convertHighDataToGraph(data, finalFormatData);
  //   const { nodes: lowNodes } = convertLowDataToGraph(data, finalFormatData);
  //   setNodesAndEdges({
  //     highNodes,
  //     lowNodes,
  //   });
  // }, [data]);

  useEffect(() => {
    if (graphRef.current) {
      graphRef.current?.clear();
    }
    if (containerRef.current) {
      const container = containerRef.current;
      graphRef.current = new Graph({
        // theme: "dark", // 或 'light'
        // background: "#000",
        container: container,
        width: container.clientWidth,
        height: container.clientHeight,
        pixelRatio: window.devicePixelRatio || 1, // 关键
        modes: {
          // default: [{ type: 'drag-canvas', enableOptimize: true }, 'zoom-canvas', 'click-select'],
          default: ['drag-canvas', 'zoom-canvas', 'click-select'],
        },
      });

      graphRef.current.on('viewportchange', () => {
        graphRef.current?.paint();
      });

      // 拖拽/缩放时暂停自动绘制，结束时重绘
      ['node:dragstart', 'canvas:dragstart'].forEach(evt => {
        graphRef.current?.on(evt, () => graphRef.current?.setAutoPaint(false));
      });
      ['node:dragend', 'canvas:dragend'].forEach(evt => {
        graphRef.current?.on(evt, () => {
          graphRef.current?.setAutoPaint(true);
          graphRef.current?.paint();
        });
      });
      graphRef.current?.on('wheelzoomend', () => graphRef.current?.paint());

      graphRef.current.on('node:click', e => {
        const shapeName = e.target.get('name');
        const nodeModel = e.item!.getModel();
        const value: any = nodeModel?.value;

        setCircuitInfo({
          id: nodeModel?.id,
          name: value?.name,
          switchStatus: value?.switchStatus,
        });
        // 点击节点事件
        // 如果点击的电压电流则弹窗展示数据
        if (shapeName.startsWith('current') || shapeName.startsWith('voltage')) {
          setShowCircuitModal(true);
        }
        if (shapeName.startsWith('img') && !isNil(value?.switchType) && value?.switchType !== SwitchType.NONE) {
          setControlSwitchModal(true);
        }
      });

      // 监听尺寸变化（数据回来后再监听）
      resizeObserverRef.current = new ResizeObserver(() => {
        if (!graphRef.current) return;
        const width = container.clientWidth;
        const height = container.clientHeight;
        graphRef.current.changeSize(width, height);
      });
      resizeObserverRef.current.observe(container);

      // 卸载时清理
      return () => {
        resizeObserverRef.current?.disconnect();
        graphRef.current?.destroy();
      };
    }
  }, []);

  // useEffect(() => {
  //   if (!graphRef.current || !containerRef.current) return;
  //   const container = containerRef.current;
  //   const width = container.clientWidth;
  //   const height = container.clientHeight;
  //   graphRef.current.changeSize(width, height);
  //   if (isFullScreen) {
  //     graphRef.current?.fitCenter();
  //     graphRef.current?.fitView();
  //   } else {
  //     setPadding(graphRef.current, { left: 20, top: 50, bottom: 20 });
  //   }
  // }, [isFullScreen]);

  useEffect(() => {
    if (!graphRef.current && !data && !dataProperty) return;
    // 自适应模式
    if (showType == 'B') {
      if (isFullScreen) {
        const container = containerRef.current;
        const width = container!.clientWidth;
        const height = container!.clientHeight;
        graphRef.current?.changeSize(width, height);
      }
      const newData = _.cloneDeep(data);
      const width = graphRef.current?.get('width');
      console.log('画布宽度:', width, newData);
      // 拆成多份list
      const formatList = splitRowsByWidth(newData!, width);

      const finalFormatData = formatData(newData);
      const { nodes: highNodes } = convertHighDataToGraph(finalFormatData, dataProperty, {
        list: formatList,
        tree: newData?.tree,
      });
      const { nodes: lowNodes } = convertLowDataToGraph(finalFormatData, dataProperty, {
        list: formatList,
        tree: newData?.tree,
      });

      // 剩下的tree画节点
      const restTreeNodes = convertRestTreeToGraph([...highNodes, ...lowNodes], newData?.tree, dataProperty);

      const seen = new Set();
      const resultHighNodes = highNodes.filter((item: any) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      });
      const resultLowNodes = lowNodes.filter((item: any) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      });

      // 高压节点处理
      const formatHighNodes = _.cloneDeep(resultHighNodes);
      const formatLowNodes = _.cloneDeep(resultLowNodes);

      // 节点分行
      const formatDataBySplit = formatAllNodes([...formatHighNodes, ...formatLowNodes, ...restTreeNodes]);

      // 分行连接桩连接
      const formatEdges = formatAllEdges(formatDataBySplit, graphRef.current!);

      graphRef.current?.data({
        nodes: formatDataBySplit,
        edges: formatEdges,
      });
      graphRef.current?.render();
      setPadding(graphRef.current!, { left: 30, top: 50, bottom: 20 });
    } else {
      // 正常模式
      const newData = _.cloneDeep(data);
      const finalFormatData = formatData(newData);

      const { nodes: highNodes } = convertHighDataToGraph(finalFormatData, dataProperty, newData);

      const { nodes: lowNodes } = convertLowDataToGraph(finalFormatData, dataProperty, newData);

      // 剩下的tree画节点
      const restTreeNodes = convertRestTreeToGraph([...highNodes, ...lowNodes], newData?.tree, dataProperty);

      const seen = new Set();
      const resultHighNodes = highNodes.filter((item: any) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      });
      const resultLowNodes = lowNodes.filter((item: any) => {
        if (seen.has(item.id)) return false;
        seen.add(item.id);
        return true;
      });
      // 高压节点处理
      const formatHighNodes = _.cloneDeep(resultHighNodes);
      // 低压节点处理
      const formatLowNodes = _.cloneDeep(resultLowNodes);

      // 节点分行
      const formatDataBySplit = formatAllNodes([...formatHighNodes, ...formatLowNodes, ...restTreeNodes]);

      // 分行连接桩连接
      const formatEdges = formatAllEdges(formatDataBySplit, graphRef.current!);

      graphRef.current!.data({
        edges: formatEdges,
        nodes: formatDataBySplit,
      });
      graphRef.current?.render();

      setPadding(graphRef.current!, { left: 30, top: 50, bottom: 20 });
      if (isFullScreen) {
        const container = containerRef.current;
        const width = container!.clientWidth;
        const height = container!.clientHeight;
        graphRef.current?.changeSize(width, height);
        graphRef.current?.fitCenter();
        graphRef.current?.fitView();
      }
    }
  }, [showType, data, dataProperty]);

  return (
    <>
      <div
        className={styles.wrapper}
        style={{
          height: isFullScreen ? '100vh' : 'calc(100vh - 216px)',
          position: isFullScreen ? 'fixed' : 'relative',
          top: 0,
          left: 0,
          zIndex: 100,
        }}
        ref={containerRef}
      >
        <div className={styles.fullScreen_btn}>
          <Space size={16}>
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={() => navigate('/power/station/configuration/OperationLog')}
            >
              操作日志
            </Button>
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={() => {
                setShowType('A');
              }}
              disabled={showType === 'A'}
            >
              正常模式
            </Button>
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={() => {
                setShowType('B');
              }}
              disabled={showType === 'B'}
            >
              自适应模式
            </Button>
            <Button
              type="link"
              style={{ padding: 0, display: 'flex', gap: 2 }}
              icon={<FullscreenOutlined />}
              onClick={fullBtn}
            >
              {!isFullScreen ? '全屏' : '退出全屏'}
            </Button>
          </Space>
        </div>
      </div>
      <CircuitDataModal
        visible={showCircuitModal}
        onCancel={() => {
          setCircuitInfo(undefined);
          setShowCircuitModal(false);
        }}
        circuitId={circuitInfo?.id}
        circuitName={circuitInfo?.name}
      />
      <SwitchControlModal
        visible={controlSwitchModal}
        onCancel={() => {
          setCircuitInfo(undefined);
          setControlSwitchModal(false);
        }}
        onOk={(status: SwitchStatusType) => {
          graphRef.current?.updateItem(circuitInfo?.id?.toString(), {
            value: {
              switchStatus: status,
            },
          });
        }}
        circuitInfo={circuitInfo}
      />
    </>
  );
};

export default WiringDiagramHighV;
